<template>
  <div>
    <!-- Overlay -->
    <transition name="fade">
      <div
        v-if="isActive"
        class="pm-overlay"
        @click.stop="isActive = false"
      ></div>
    </transition>

    <!-- Drawer -->
    <transition name="slide">
      <div
        v-if="isActive"
        class="pm-drawer"
        @click.stop
        :class="{ 'pm-drawer--mobile': isMobile }"
      >
        <!-- Header with image -->
        <div class="pm-header" :style="headerStyle">
          <div class="pm-header__close" @click="isActive = false">
            <feather-icon icon="XIcon" svgClasses="w-5 h-5" />
          </div>
          <div class="pm-header__info">
            <h3 class="pm-header__title">{{ item.title }}</h3>
            <p class="pm-header__sub">Wähle deine Beilagen</p>
          </div>
        </div>

        <!-- Scrollable content -->
        <div class="pm-body">
          <!-- Sauce -->
          <div
            v-if="item.sauces.length > 0"
            class="pm-section"
            :class="{ chosen: selectedSauces.length > 0 }"
          >
            <div class="pm-section__head">
              <div class="flex items-center">
                <img src="/icons/sauce.svg" class="pm-section__icon" />
                <div class="ml-2">
                  <span class="pm-section__label">Sauce</span>
                  <span class="pm-section__hint">Bitte wählen</span>
                </div>
              </div>
              <feather-icon
                v-if="selectedSauces.length > 0"
                icon="CheckCircleIcon"
                svgClasses="w-5 h-5"
                style="color: #2ecc71"
              />
            </div>
            <div class="pm-chips">
              <span
                v-for="s in item.sauces"
                :key="s._id"
                class="pm-chip"
                :class="{ selected: isIn(s, selectedSauces) }"
                @click="toggle(s, 'selectedSauces')"
              >
                {{ s.name }}<small v-if="s.price > 0"> +{{ s.price }}€</small>
              </span>
            </div>
          </div>

          <!-- Salat -->
          <div
            v-if="item.salads.length > 0"
            class="pm-section"
            :class="{ chosen: selectedSalads.length > 0 }"
          >
            <div class="pm-section__head">
              <div class="flex items-center">
                <img src="/icons/salad.svg" class="pm-section__icon" />
                <div class="ml-2">
                  <span class="pm-section__label">Salat</span>
                  <span class="pm-section__hint">Bitte wählen</span>
                </div>
              </div>
              <feather-icon
                v-if="selectedSalads.length > 0"
                icon="CheckCircleIcon"
                svgClasses="w-5 h-5"
                style="color: #2ecc71"
              />
            </div>
            <div class="pm-chips">
              <span
                v-for="s in item.salads"
                :key="s._id"
                class="pm-chip"
                :class="{ selected: isIn(s, selectedSalads) }"
                @click="toggle(s, 'selectedSalads')"
              >
                {{ s.name }}<small v-if="s.price > 0"> +{{ s.price }}€</small>
              </span>
            </div>
          </div>

          <!-- Fleisch -->
          <div
            v-if="item.meats.length > 0"
            class="pm-section"
            :class="{ chosen: selectedMeats.length > 0 }"
          >
            <div class="pm-section__head">
              <div class="flex items-center">
                <img src="/icons/meat.svg" class="pm-section__icon" />
                <div class="ml-2">
                  <span class="pm-section__label">Fleisch</span>
                  <span class="pm-section__hint">Bitte wählen</span>
                </div>
              </div>
              <feather-icon
                v-if="selectedMeats.length > 0"
                icon="CheckCircleIcon"
                svgClasses="w-5 h-5"
                style="color: #2ecc71"
              />
            </div>
            <div class="pm-chips">
              <span
                v-for="m in item.meats"
                :key="m._id"
                class="pm-chip"
                :class="{ selected: isIn(m, selectedMeats) }"
                @click="toggle(m, 'selectedMeats')"
              >
                {{ m.name }}<small v-if="m.price > 0"> +{{ m.price }}€</small>
              </span>
            </div>
          </div>

          <!-- Extra -->
          <div
            v-if="item.extras.length > 0"
            class="pm-section"
            :class="{ chosen: selectedExtras.length > 0 }"
          >
            <div class="pm-section__head">
              <div class="flex items-center">
                <img src="/icons/extra.svg" class="pm-section__icon" />
                <div class="ml-2">
                  <span class="pm-section__label">Extra</span>
                  <span class="pm-section__hint">Optional</span>
                </div>
              </div>
              <feather-icon
                v-if="selectedExtras.length > 0"
                icon="CheckCircleIcon"
                svgClasses="w-5 h-5"
                style="color: #2ecc71"
              />
            </div>
            <div class="pm-chips">
              <span
                v-for="e in item.extras"
                :key="e._id"
                class="pm-chip"
                :class="{ selected: isIn(e, selectedExtras) }"
                @click="toggle(e, 'selectedExtras')"
              >
                {{ e.name }}<small v-if="e.price > 0"> +{{ e.price }}€</small>
              </span>
            </div>
          </div>

          <!-- Beilage -->
          <div
            v-if="item.pomm_rice.length > 0"
            class="pm-section"
            :class="{ chosen: selectedPommRice !== null }"
          >
            <div class="pm-section__head">
              <div class="flex items-center">
                <img src="/icons/pomm_rice.svg" class="pm-section__icon" />
                <div class="ml-2">
                  <span class="pm-section__label">Beilage</span>
                  <span class="pm-section__hint">Bitte wählen</span>
                </div>
              </div>
              <feather-icon
                v-if="selectedPommRice"
                icon="CheckCircleIcon"
                svgClasses="w-5 h-5"
                style="color: #2ecc71"
              />
            </div>
            <div class="pm-chips">
              <span
                v-for="p in item.pomm_rice"
                :key="p._id"
                class="pm-chip"
                :class="{
                  selected: selectedPommRice && selectedPommRice._id === p._id,
                }"
                @click="single(p, 'selectedPommRice')"
              >
                {{ p.name }}<small v-if="p.price > 0"> +{{ p.price }}€</small>
              </span>
            </div>
          </div>

          <!-- Getränk -->
          <div
            v-if="item.drinks.length > 0"
            class="pm-section"
            :class="{ chosen: selectedDrink !== null }"
          >
            <div class="pm-section__head">
              <div class="flex items-center">
                <img src="/icons/drink.svg" class="pm-section__icon" />
                <div class="ml-2">
                  <span class="pm-section__label">Getränk</span>
                  <span class="pm-section__hint">Optional</span>
                </div>
              </div>
              <feather-icon
                v-if="selectedDrink"
                icon="CheckCircleIcon"
                svgClasses="w-5 h-5"
                style="color: #2ecc71"
              />
            </div>
            <div class="pm-chips">
              <span
                v-for="d in item.drinks"
                :key="d._id"
                class="pm-chip"
                :class="{
                  selected: selectedDrink && selectedDrink._id === d._id,
                }"
                @click="single(d, 'selectedDrink')"
              >
                {{ d.name }}<small v-if="d.price > 0"> +{{ d.price }}€</small>
              </span>
            </div>
          </div>
        </div>

        <!-- Fixed bottom button -->
        <div class="pm-footer" @click.stop="onAddToCart">
          <feather-icon icon="ShoppingCartIcon" svgClasses="w-5 h-5 mr-2" />
          <span>IN DEN EINKAUFSWAGEN</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { logViewItem } from "../../../analytics";
export default {
  props: {
    item: { type: Object, required: true },
    active: { type: Boolean, required: true },
    cartItems: { type: Array, required: true },
  },
  data() {
    return {
      selectedSauces: [],
      selectedMeats: [],
      selectedSalads: [],
      selectedExtras: [],
      selectedPommRice: null,
      selectedDrink: null,
    };
  },
  computed: {
    isActive: {
      get() {
        return this.active;
      },
      set(val) {
        this.$emit("update:active", val);
      },
    },
    isMobile() {
      return window.innerWidth < 768;
    },
    headerStyle() {
      if (this.item.photo) {
        return {
          backgroundImage:
            "linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(18,18,18,0.95)), url(" +
            this.item.photo +
            ")",
          backgroundSize: "cover",
          backgroundPosition: "center",
        };
      }
      return { background: "linear-gradient(135deg, #C41E2A, #8B1520)" };
    },
  },
  watch: {
    active(val) {
      if (val) {
        this.reset();
        logViewItem(this.item);
      }
    },
  },
  methods: {
    isIn(item, list) {
      return list.some((s) => s._id === item._id);
    },
    toggle(item, key) {
      const idx = this[key].findIndex((s) => s._id === item._id);
      if (idx >= 0) this[key].splice(idx, 1);
      else
        this[key].push({ label: item.name, price: item.price, _id: item._id });
    },
    single(item, key) {
      this[key] =
        this[key] && this[key]._id === item._id
          ? null
          : { label: item.name, price: item.price, _id: item._id };
    },
    reset() {
      this.selectedSauces = [];
      this.selectedMeats = [];
      this.selectedSalads = [];
      this.selectedExtras = [];
      this.selectedPommRice = null;
      this.selectedDrink = null;
    },
    onAddToCart() {
      if (this.item.sauces.length > 0 && this.selectedSauces.length === 0) {
        this.$vs.notify({
          title: "Sauce fehlt",
          text: "Bitte wähle mindestens eine Sauce",
          color: "danger",
        });
        return;
      }
      if (this.item.meats.length > 0 && this.selectedMeats.length === 0) {
        this.$vs.notify({
          title: "Fleisch fehlt",
          text: "Bitte wähle ein Fleisch",
          color: "danger",
        });
        return;
      }
      if (this.item.salads.length > 0 && this.selectedSalads.length === 0) {
        this.$vs.notify({
          title: "Salat fehlt",
          text: "Bitte wähle einen Salat",
          color: "danger",
        });
        return;
      }
      if (this.item.pomm_rice.length > 0 && !this.selectedPommRice) {
        this.$vs.notify({
          title: "Beilage fehlt",
          text: "Bitte wähle eine Beilage",
          color: "danger",
        });
        return;
      }

      const payload = {
        ...this.item,
        selectedSauces: this.selectedSauces,
        selectedMeats: this.selectedMeats,
        selectedSalads: this.selectedSalads,
        selectedExtras: this.selectedExtras,
        selectedPommRice: this.selectedPommRice,
        selectedDrink: this.selectedDrink,
      };
      const existing = this.cartItems.find((c) => c._id === this.item._id);
      if (
        existing &&
        JSON.stringify(payload.selectedSauces) ===
          JSON.stringify(existing.selectedSauces)
      ) {
        this.$store.commit("shop/UPDATE_ITEM_QUANTITY", {
          cartItemID: existing.cartItemID,
          newValue: existing.quantity + 1,
        });
      } else {
        this.$store.dispatch("shop/additemInCart", payload);
      }
      this.$emit("onAddedToCart");
      this.isActive = false;
    },
  },
};
</script>

<style lang="scss">
.pm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 51000;
}

.pm-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  height: 100dvh;
  height: 100vh; /* fallback for older browsers */

  @supports (height: 100dvh) {
    height: 100dvh;
  }

  background: var(--pok-bg);
  z-index: 51001;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.5);

  &--mobile {
    width: 100vw;
  }
}

.pm-header {
  height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  flex-shrink: 0;

  &__close {
    position: absolute;
    top: 16px;
    left: 16px;
    background: rgba(0, 0, 0, 0.4);
    border-radius: 50%;
    padding: 8px;
    cursor: pointer;
    color: #fff;
  }

  &__info {
    padding: 20px;
  }
  &__title {
    color: #fff;
    font-size: 24px;
    font-weight: 800;
  }
  &__sub {
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
  }
}

.pm-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.pm-section {
  background: var(--pok-card);
  border-radius: 16px;
  border: 1px solid var(--pok-border);
  padding: 16px;
  margin-bottom: 12px;

  &.chosen {
    border-color: rgba(46, 204, 113, 0.4);
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  &__icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  &__label {
    color: var(--pok-text);
    font-size: 15px;
    font-weight: 700;
    display: block;
  }
  &__hint {
    color: var(--pok-text-muted);
    font-size: 11px;
  }
}

.pm-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pm-chip {
  padding: 10px 14px;
  border-radius: 12px;
  background: var(--pok-card-light);
  border: 1px solid var(--pok-border);
  color: var(--pok-text);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;

  small {
    color: var(--pok-text-secondary);
  }

  &.selected {
    background: #c41e2a;
    border-color: #c41e2a;
    color: #fff;
    small {
      color: rgba(255, 255, 255, 0.7);
    }
  }

  &:hover:not(.selected) {
    border-color: var(--pok-text-muted);
  }
}

.pm-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #c41e2a;
  color: #fff;
  padding: 18px;
  margin: 12px 16px 24px;
  border-radius: 14px;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    opacity: 0.9;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}
.slide-enter,
.slide-leave-to {
  transform: translateX(100%);
}
</style>
